
import { formatInTimeZone } from 'date-fns-tz';

// List of common timezones for the selector
export const commonTimezones = [
  "America/New_York", // Eastern Time
  "America/Chicago", // Central Time
  "America/Denver", // Mountain Time
  "America/Los_Angeles", // Pacific Time
  "America/Anchorage", // Alaska Time
  "Pacific/Honolulu", // Hawaii Time
  "America/Toronto", // Eastern Canada
  "Europe/London", // UK
  "Europe/Paris", // Central Europe
  "Europe/Berlin", // Germany
  "Europe/Moscow", // Russia
  "Asia/Dubai", // UAE
  "Asia/Kolkata", // India
  "Asia/Shanghai", // China
  "Asia/Tokyo", // Japan
  "Asia/Singapore", // Singapore
  "Australia/Sydney", // Australia Eastern
  "Pacific/Auckland", // New Zealand
];

// Format a date with timezone consideration
export const formatInUserTimezone = (
  date: Date,
  timezone: string,
  formatString: string
): string => {
  return formatInTimeZone(date, timezone, formatString);
};

// Convert time from one timezone to another
export const convertTimezone = (
  date: Date,
  fromTimezone: string,
  toTimezone: string
): Date => {
  // Get the current offset in minutes for the two timezones
  const fromOffset = getTimezoneOffset(date, fromTimezone);
  const toOffset = getTimezoneOffset(date, toTimezone);
  
  // Calculate the difference in milliseconds
  const offsetDiff = (fromOffset - toOffset) * 60 * 1000;
  
  // Create a new date with the adjusted time
  return new Date(date.getTime() + offsetDiff);
};

// Get timezone offset in minutes for a specific timezone
const getTimezoneOffset = (date: Date, timezone: string): number => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    hour12: false,
  });
  
  const parts = formatter.formatToParts(date);
  const localDate = new Date(
    parseInt(parts.find(p => p.type === 'year')?.value || '0'),
    parseInt(parts.find(p => p.type === 'month')?.value || '0') - 1,
    parseInt(parts.find(p => p.type === 'day')?.value || '0'),
    parseInt(parts.find(p => p.type === 'hour')?.value || '0'),
    parseInt(parts.find(p => p.type === 'minute')?.value || '0'),
    parseInt(parts.find(p => p.type === 'second')?.value || '0')
  );
  
  // Return the difference in minutes
  return (date.getTime() - localDate.getTime()) / (60 * 1000);
};

// Get a friendly name for a timezone with current offset
export const getTimezoneDisplayName = (timezone: string): string => {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
    });
    
    const parts = formatter.formatToParts(now);
    const timeZonePart = parts.find(part => part.type === 'timeZoneName');
    const timeZoneName = timeZonePart ? timeZonePart.value : '';
    
    // Calculate the offset
    const offset = getTimezoneOffset(now, timezone);
    const offsetHours = Math.floor(Math.abs(offset) / 60);
    const offsetMinutes = Math.abs(offset) % 60;
    
    const offsetStr = `${offset >= 0 ? '+' : '-'}${offsetHours.toString().padStart(2, '0')}:${offsetMinutes.toString().padStart(2, '0')}`;
    
    return `${timezone.split('/').pop()?.replace('_', ' ')} (GMT${offsetStr}, ${timeZoneName})`;
  } catch (error) {
    return timezone;
  }
};

// Detect user's timezone
export const getUserTimezone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};
